/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, ModalHeader, ModalBody, Divisor } from '../../components/Modais/styles';
import useStore from '../../services/hooks/useStore';
import { observer } from 'mobx-react-lite';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import Formatter from '../../services/Formatter';
import { Box, TextField, Typography } from '@material-ui/core';
import Botao from '../../components/Botoes/Botao';
import { KeyboardDateTimePicker } from '@material-ui/pickers';
import InputNumero from '../../components/InputNumero';
import { RequestData } from './CadastrarAguaReservatorioMuitosReservatorios';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';

type Props = ModalProps & {
  excluirReservatorioIds: number[];
  parametroValor: ParametroValor[] | null;
  date: MaterialUiPickersDate;
  onConfirm(dados: RequestData): void;
  editData: RequestData | null;
};

const SelecionarReservatorios: React.FC<Props> = ({
  excluirReservatorioIds,
  parametroValor,
  date: dateParam,
  open,
  handleClose,
  onConfirm,
  editData,
}) => {
  const store = useStore();

  const finalDate = editData?.data ? Formatter.stringToDate(editData?.data) : dateParam;
  const etapaInicial = editData ? 1 : 0;
  const [etapa, setEtapa] = useState(etapaInicial);
  const [reservatorioSelecionado, setReservatorioSelecionado] = useState<Reservatorio | null>(null);
  const [pesquisa, setPesquisa] = useState('');
  const [date, setDate] = useState<MaterialUiPickersDate>(finalDate);

  const reservatoriosFiltrados = useMemo(
    () =>
      store.reservatorios.arr
        ?.filter((t) => !excluirReservatorioIds.includes(t.id))
        ?.filter((t) => t.nome?.includes(pesquisa))
        ?.sort((a, b) => {
          const textA = a.nome.toUpperCase();
          const textB = b.nome.toUpperCase();
          return textA < textB ? -1 : textA > textB ? 1 : 0;
        }),
    [pesquisa, store.reservatorios.arr],
  );

  function obterParametros() {
    // Obs.: copiado das funções já existentes no arquivo CadastraQualidade.tsx
    const parametro_input: any = document.querySelectorAll('.parametro_input[type=text]');
    const parametros_utilizados = [];

    for (let index = 0; index < parametro_input.length; index++) {
      if (parametro_input[index].value.replace('.', '').replace(',', '.') > 0) {
        parametros_utilizados.push({
          parametro: parametro_input[index].id,
          value: parametro_input[index].value.replace('.', '').replace(',', '.'),
        });
      }
    }
    return parametros_utilizados;
  }

  function avancarEtapa() {
    setEtapa((prev) => prev + 1);
  }

  function voltarEtapa() {
    if (etapa > 0) setEtapa((prev) => prev - 1);
  }

  function selecionarReservatorio(reservatorio: Reservatorio) {
    if (reservatorio.uuid !== reservatorioSelecionado?.uuid) {
      setReservatorioSelecionado(reservatorio);
      avancarEtapa();
    } else {
      setReservatorioSelecionado(null);
    }
  }

  function handleConfirm() {
    if (reservatorioSelecionado) {
      const parametros_utilizados = obterParametros();
      const dados = {
        reservatorio: reservatorioSelecionado.id,
        data: Formatter.dateToString(date),
        parametros_utilizados,
      };
      onConfirm(dados);
      handleClose();
    }
  }

  useEffect(() => {
    const reservatorios = store.reservatorios.arr;
    if (!reservatorios || reservatorios?.length === 0) store.reservatorios.populate();
  }, []);

  useEffect(() => {
    if (editData) {
      const reservatorioEncontrado = store.reservatorios.arr?.find((t) => t.id === editData.reservatorio);
      setReservatorioSelecionado(reservatorioEncontrado ?? null);
    }
  }, [editData, store.reservatorios.arr]);

  function renderizarReservatorios() {
    return (
      <>
        <Box width="100%" padding="10px" display="flex">
          <TextField label="Pesquisa Pelo Nome" value={pesquisa} onChange={(e) => setPesquisa(e.target.value)} />
        </Box>
        <Box style={{ overflowY: 'scroll' }} padding={1} width={600} height={400}>
          {reservatoriosFiltrados?.map((reservatorio: Reservatorio) => {
            let selecionado = reservatorio.uuid === reservatorioSelecionado?.uuid;

            return (
              <Box
                key={reservatorio.uuid}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                width="35rem"
                height="4rem"
                mb={1}
                pr={2}
                borderRadius={5}
                boxShadow={4}
                onClick={() => selecionarReservatorio(reservatorio)}
                style={{ cursor: 'pointer' }}
              >
                <Box display="flex" alignItems="center">
                  {selecionado && <CheckCircleIcon htmlColor="#00C853" />}
                  <Box ml={selecionado ? 0 : 3}>
                    {reservatorio.nome}
                    <Typography style={{ color: '#00C853' }}>
                      {Formatter.formatNumber(3, reservatorio.volume)} (lt/m³)
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  <Typography align="right">{reservatorio.origem.nome}</Typography>
                  <Typography align="right" style={{ color: '#00C853' }}>
                    {reservatorio.situacao.nome}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box width="100%" display="flex" padding="10px">
          <Botao onClick={handleClose} variant="contained" cor="gray">
            Voltar
          </Botao>
          <Botao onClick={avancarEtapa} disabled={!reservatorioSelecionado} variant="contained" cor="#00C853">
            Avançar
          </Botao>
        </Box>
      </>
    );
  }

  function renderizarParametros() {
    return (
      <Box width="540px">
        <TextField label="Reservatórios selecionados" disabled value={reservatorioSelecionado?.nome} />
        <KeyboardDateTimePicker
          autoOk
          disableToolbar
          ampm={false}
          variant="inline"
          margin="normal"
          label="Data da análise"
          value={date}
          onChange={(d) => setDate(d)}
          onError={(error) => console.log(error)}
          format="dd/MM/yyyy HH:mm"
        />
        <Divisor>
          <p>Parâmetros</p>
        </Divisor>

        {!editData &&
          parametroValor?.map((parametro) => {
            return (
              <div key={parametro.id}>
                {parametro.used ? (
                  <InputNumero
                    value={0}
                    precision="3"
                    label={parametro.parametro.nome}
                    className="parametro_input"
                    id={parametro.parametro.id.toString()}
                  />
                ) : null}
              </div>
            );
          })}

        {editData &&
          store.parametroValor?.map((item) => {
            if (item.used) {
              const parametro_utilizado = editData.parametros_utilizados.find(
                (parametro) => parametro.parametro == item.parametro.id,
              );
              const valor = parametro_utilizado ? Number(parametro_utilizado.value) : 0;
              return (
                <div key={item.id}>
                  <InputNumero
                    value={valor}
                    precision="3"
                    label={item.parametro.nome}
                    className="parametro_input"
                    id={item.parametro.id.toString()}
                  />
                </div>
              );
            } else {
              return null;
            }
          })}

        <Box width="100%" display="flex" p="10px">
          <Botao onClick={editData ? handleClose : voltarEtapa} variant="contained" cor="gray">
            Voltar
          </Botao>
          <Botao onClick={handleConfirm} variant="contained" cor="#00C853">
            Salvar
          </Botao>
        </Box>
      </Box>
    );
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalHeader>{etapa === 0 ? 'Selecione um Reservatório' : 'Parâmetros da análise'}</ModalHeader>
      <ModalBody>
        {etapa === 0 && renderizarReservatorios()}
        {etapa === 1 && renderizarParametros()}
      </ModalBody>
    </Modal>
  );
};

export default observer(SelecionarReservatorios);
